import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faPhone } from '@fortawesome/pro-light-svg-icons';
import { Branch, SeoExam, SeoSpecialty } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { useCopyNumber } from './_hooks/use-copy-number.hook';

import { Button } from '@/components/ui/button/button.component';
import { Link } from '@/components/ui/link/link.component';
import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import useDeviceType, { DeviceType } from '@/hooks/use-device-type.hook';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { maskPhone } from '@/utils/formatters/phonenumber';
import { Place } from '@/types/google-maps.types';
import { formatBranchName } from '@/utils/formatters/branch';
import { Typography } from '@/components/ui/typography/typography.component';
import { useWhatsappLinkSimple } from '@/hooks/use-whatsapp/use-whatsapp-link-simple.hook';
import { scheduleProcedureWhatsappMessage } from '@/utils/consts/cta-whatsapp-messages.consts';
import { SearchType } from '@/types/most-search-type';

export interface BranchShareItemProps {
  branch?: Branch | null;
  place: Pick<Place, 'state' | 'city'>;
  procedure?: SeoSpecialty | SeoExam | null;
  searchType: SearchType;
}

export default function BranchShareItem({
  branch,
  place,
  procedure,
  searchType,
}: BranchShareItemProps) {
  const deviceType = useDeviceType();
  const { copyNumber, isCopyingLoading } = useCopyNumber();

  const message
    = scheduleProcedureWhatsappMessage[searchType as keyof typeof scheduleProcedureWhatsappMessage]?.({
      procedureName: String(procedure?.name),
      city: place?.city,
      state: place?.state,
    }) ?? '';
  const branchWhatsapp = branch?.whatsapp || '';

  const { resolveLink } = useWhatsappLinkSimple(branchWhatsapp, message);
  const whatsappLink = resolveLink(branchWhatsapp);

  const branchPhone = branch?.phones?.[0];

  return (
    <>
      <div className="flex items-center gap-4">
        <EntityAvatar type={EntityTypeEnum.BRANCH} alt="Unidade Física" width={52} height={52} />

        <Typography className="text-lg text-gray-900 font-bold leading-6">
          {formatBranchName(branch?.name ?? '')}
        </Typography>
      </div>

      {branch?.whatsapp
        ? (
            <Button variant="secondary" className="mt-6" startIcon={faWhatsapp} asChild>
              <Link
                prefetch={false}
                data-testid={DataTestIdEnum.SEO_CTA_MULTIPLE_BRANCHES_MODAL_WHATSAPP_BUTTON}
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {maskPhone(branch.whatsapp)}
              </Link>
            </Button>
          )
        : (
            <>
              {deviceType === DeviceType.Desktop && branchPhone
                ? (
                    <Button
                      data-testid={DataTestIdEnum.SEO_CTA_MULTIPLE_BRANCHES_MODAL_COPY_PHONE_BUTTON}
                      variant="secondary"
                      startIcon={faPhone}
                      className="mt-6"
                      disabled={isCopyingLoading}
                      isLoading={isCopyingLoading}
                      onClick={() => copyNumber(branchPhone)}
                    >
                      {maskPhone(String(branch?.phones?.[0]))}
                    </Button>
                  )
                : branchPhone
                  ? (
                      <Button
                        data-testid={DataTestIdEnum.SEO_CTA_MULTIPLE_BRANCHES_MODAL_PHONE_BUTTON}
                        variant="secondary"
                        startIcon={faPhone}
                        className="mt-6"
                        asChild
                      >
                        <a href={`tel:+55${branchPhone}`} data-testid="tel-button">
                          {maskPhone(branchPhone)}
                        </a>
                      </Button>
                    )
                  : null}
            </>
          )}
    </>
  );
}
